<script>
  /**
   * @event {null | number | string} change
   * @event {null | number | string} input
   */

  /**
   * Set the size of the input field. The default is `large`.
   * @type {"small" | "medium" | "large"}
   */
  export let size = "large";

  /**
   * @type {string}
   */
  export let type = "text";

  /**
   * Specify the input value.
   *
   * `value` will be set to `null` if type="number"
   * and the value is empty.
   * @type {null | number | string}
   */
  export let value = "";

  /** Specify the placeholder text */
  export let placeholder = "";

  /** Set to `true` to disable the input */
  export let disabled = false;

  /** Set an id for the input element */
  export let id = "ccs-" + Math.random().toString(36);

  /**
   * Specify a name attribute for the input
   * @type {string}
   */
  export let name = undefined;

  /** Specify the label text */
  export let labelText = "";

  /**
   * Set to `true` to make the label Large
   * @type {boolean}
   */
  export let labelLarge = false;

  /** Set to `true` to indicate an invalid state */
  export let invalid = false;

  /** Specify the invalid state text */
  export let invalidText = "";

  /** Obtain a reference to the input HTML element */
  export let ref = null;

  /** Set to `true` to mark the field as required */
  export let required = false;

  /** Set to `true` to use the inline variant */
  export let inline = false;

  /** Set to `true` to use the read-only variant */
  export let readonly = false;

  /**
   * 
   * @type {boolean}
   * @default true
   */
  export let clearInvalidOnFocus = true;

  /**
   * @type {string | undefined}
   * @default ''
   */
  export let autocomplete = undefined;

  /**
   * @type {string?}
   */
  export let description = null;

  /**
   * @type {string}
   * @default ''
   */
  export let tooltipText = "";

  /**
   * @type {string}
   * @default 'right'
   */
  export let tooltipDirection = "right";

  import { createEventDispatcher, getContext } from "svelte";
  import Tooltip from "../Tooltip/Tooltip.svelte";
  import WarningFilled from "../icons/WarningFilled.svelte";
  import EditOff from "../icons/EditOff.svelte";

  const ctx = getContext("Form");
  const dispatch = createEventDispatcher();

  const removePreviousErrors = () => {
    if (!clearInvalidOnFocus) return;
    if (invalid) {
      description = "";
    }
    invalid = false; // reset invalid when the user clicks on the form element
  };

  function parse(raw) {
    if ($$restProps.type !== "number") return raw;
    return raw != "" ? Number(raw) : null;
  }

  /** @type {(e: Event) => void} */
  const onInput = (e) => {
    value = parse(e.target.value);
    dispatch("input", value);
  };

  /** @type {(e: Event) => void} */
  const onChange = (e) => {
    dispatch("change", parse(e.target.value));
  };

  let isShaking = false;

  function shake() {
    isShaking = true;
    setTimeout(() => {
      isShaking = false;
    }, 500);
  }

  $: isFluid = !!ctx && ctx.isFluid;
  $: error = invalid && !readonly;
  $: helperId = `helper-${id}`;
  $: errorId = `error-${id}`;
  $: if (invalid && !isShaking) {
    shake();
  }
</script>

<!-- svelte-ignore a11y-mouse-events-have-key-events -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class:bx--form-item="{true}"
  class:bx--text-input-wrapper="{true}"
  class:bx--text-input-wrapper--inline="{inline}"
  class:bx--text-input-wrapper--readonly="{readonly}"
  on:click
  on:mouseover
  on:mouseenter
  on:mouseleave
>
  {#if inline}
    <div class:bx--text-input__label-helper-wrapper="{true}">
      {#if labelText}
        <label
          for="{id}"
          class:bx--label="{true}"
          class:bx--visually-hidden="{!labelText}"
          class:bx--label--disabled="{disabled}"
          class:bx--label--large="{labelLarge}"
          class:bx--label--inline="{inline}"
          class:bx--label--inline--sm="{size === "small"}"
          class:bx--label--inline--md="{size === "medium"}"
        >
          <slot name="labelText">
            {labelText}{required ? "*" : ""}
            {#if tooltipText}
              <Tooltip text="{tooltipText}" direction="{tooltipDirection}" />
            {/if}
          </slot>
        </label>
      {/if}
      {#if !isFluid && description}
        <div
          class:bx--form__helper-text="{true}"
          class:bx--form__helper-text--disabled="{disabled}"
          class:bx--form__helper-text--inline="{inline}"
        >
          {description}
        </div>
      {/if}
    </div>
  {/if}
  {#if !inline && (labelText || $$slots.labelText)}
    <label
      for="{id}"
      class:bx--label="{true}"
      class:bx--visually-hidden="{!labelText}"
      class:bx--label--disabled="{disabled}"
      class:bx--label--inline="{inline}"
      class:bx--label--inline-sm="{size === "small"}"
      class:bx--label--inline-md="{size === "medium"}"
      class:bx--label--large="{labelLarge}"
    >
      <slot name="labelText">
        {labelText}{required ? "*" : ""}
        {#if tooltipText}
          <Tooltip text="{tooltipText}" direction="{tooltipDirection}" />
        {/if}
      </slot>
    </label>
  {/if}
  <div
    class:bx--text-input__field-outer-wrapper="{true}"
    class:bx--text-input__field-outer-wrapper--inline="{inline}"
    class:bx--text-input__field-outer-wrapper--md="{size === "medium"}"
  >
    <div
      data-invalid="{error || undefined}"
      class:bx--text-input__field-wrapper="{true}"
    >
      {#if readonly}
        <EditOff class="bx--text-input__readonly-icon" />
      {:else if invalid}
        <WarningFilled class="bx--text-input__invalid-icon" />
      {/if}
      <input
        bind:this="{ref}"
        type="{type}"
        data-invalid="{error || undefined}"
        aria-invalid="{error || undefined}"
        aria-describedby="{error
          ? errorId
          : description
          ? helperId
          : undefined}"
        autocomplete="{autocomplete}"
        disabled="{disabled}"
        id="{id}"
        name="{name}"
        placeholder="{placeholder}"
        value="{value}"
        required="{required}"
        readonly="{readonly}"
        class:bx--text-input="{true}"
        class:bx--text-input--invalid="{error}"
        class:bx--text-input--sm="{size === "small"}"
        class:bx--text-input--md="{size === "medium"}"
        class:bx--text-input--shaking="{isShaking}"
        {...$$restProps}
        on:change="{onChange}"
        on:input="{onInput}"
        on:click="{removePreviousErrors}"
        on:focus="{removePreviousErrors}"
        on:keydown
        on:keyup
        on:focus
        on:blur
        on:paste
      />
      {#if isFluid}
        <hr class:bx--text-input__divider="{true}" />
      {/if}
      {#if isFluid && !inline && invalid}
        <div class:bx--form-requirement="{true}" id="{errorId}">
          {invalidText}
        </div>
      {/if}
    </div>
    {#if !invalid && !isFluid && !inline && description}
      <div
        id="{helperId}"
        class:bx--form__helper-text="{true}"
        class:bx--form__helper-text--disabled="{disabled}"
        class:bx--form__helper-text--inline="{inline}"
      >
        {description}
      </div>
    {/if}
    {#if !isFluid && invalid}
      <div class:bx--form-requirement="{true}" id="{errorId}">
        {invalidText}
      </div>
    {/if}
  </div>
</div>

<style>

  .bx--text-input__field-wrapper[data-invalid] ~ .bx--form-requirement {
    color: var(--pax-orange);
  }

  input::placeholder {
    color: var(--pax-black-inactive);
  }

  .bx--form__helper-text {
    padding-left: var(--xs);
  }

  .bx--label {
    font-size: var(--cds-label-01-font-size, 0.75rem);
    font-weight: 400;
    color: var(--pax-black);
    display: flex;
    gap: var(--xs);
  }

  .bx--label--large {
    font-weight: 700;
    font-size: 0.875rem;
  }

  .bx--text-input--md {
    height: 2.5rem;
  }

  .bx--text-input__field-outer-wrapper--md {
    flex-direction: column-reverse;
  }
</style>
